import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { XIcon } from '@heroicons/react/outline';
import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftDeliverySummary } from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  giftOrderListForAdminDataAtomFamily,
  giftOrderListForAdminStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { forceDeleteGiftOrder } from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftOrderListDateRange,
  useGiftOrderListForAdminHook,
} from '@shared/api/giftOrder/giftOrder.hook';
import {
  GiftOrderDeliveryMode,
  GiftOrderForAdmin,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Loading } from '@shared/components/Loading';
import { formatPrice } from '@shared/utils/formatPrice';

import { CouponDetailModal } from '../components/CouponDetailModal';
import { CreateCouponTestModal } from '../components/CreateCouponTestModal';
import { CreateDeliveryTestModal } from '../components/CreateDeliveryTestModal';
import { DeliveryDetailModal } from '../components/DeliveryDetailModal';
import { OrderRow } from '../components/OrderRow';
import { getOrderType, OrderTypeFilter } from '../utils/giftOrder.constants';

const getLocalToday = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const toDateStr = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getDatePreset = (preset: string): GiftOrderListDateRange => {
  const today = getLocalToday();
  const to = toDateStr(today);
  switch (preset) {
    case '1w': {
      const from = new Date(today);
      from.setDate(from.getDate() - 7);
      return { from: toDateStr(from), to };
    }
    case '2w': {
      const from = new Date(today);
      from.setDate(from.getDate() - 14);
      return { from: toDateStr(from), to };
    }
    case '1m': {
      const from = new Date(today);
      from.setMonth(from.getMonth() - 1);
      return { from: toDateStr(from), to };
    }
    case '3m': {
      const from = new Date(today);
      from.setMonth(from.getMonth() - 3);
      return { from: toDateStr(from), to };
    }
    default:
      return {};
  }
};

type DatePreset = '1w' | '2w' | '1m' | '3m' | 'custom';

const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  '1w': '1주',
  '2w': '2주',
  '1m': '1개월',
  '3m': '3개월',
  custom: '직접선택',
};

const FORCE_DELETE_PASSWORD = 'Admin0731';

const ForceDeleteModal: FC<{
  order: GiftOrderForAdmin;
  onClose: () => void;
  onDeleted: () => void;
}> = ({ order, onClose, onDeleted }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (password !== FORCE_DELETE_PASSWORD) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await forceDeleteGiftOrder(order._id);
      alert('주문이 강제 삭제되었습니다.');
      onDeleted();
    } catch (e) {
      alert('삭제 실패: ' + (e instanceof Error ? e.message : '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  const firstItemName = order.orderDetails[0]?.giftItemEmbed.name || '-';
  const orderName =
    order.orderDetails.length > 1
      ? `${firstItemName} 외 ${order.orderDetails.length - 1}건`
      : firstItemName;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-red-600">강제 삭제</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 rounded-lg bg-red-50 p-3">
          <p className="text-sm font-medium text-red-800">
            이 주문을 강제 삭제하시겠습니까?
          </p>
          <p className="mt-1 text-xs text-red-600">
            연결된 쿠폰, 배송 데이터가 모두 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
          </p>
        </div>

        <div className="mb-4 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
          <p>
            <span className="text-gray-500">상품:</span>{' '}
            <span className="font-medium">{orderName}</span>
          </p>
          <p>
            <span className="text-gray-500">금액:</span>{' '}
            <span className="font-medium">
              {formatPrice(order.paymentDetail.paymentByCash)}원
            </span>
          </p>
          <p>
            <span className="text-gray-500">ID:</span>{' '}
            <span className="font-mono text-xs text-gray-400">{order._id}</span>
          </p>
        </div>

        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            비밀번호 입력
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleDelete();
            }}
            placeholder="비밀번호를 입력하세요"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            autoFocus
          />
          {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleDelete}
            disabled={loading || !password}
            className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? '삭제 중...' : '강제 삭제'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const GiftOrderListPage: FC = function GiftOrderListPage() {
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'paid' | 'confirmed' | 'cancelRequested' | 'canceled'
  >('all');
  const [typeFilter, setTypeFilter] = useState<OrderTypeFilter>('all');
  const [datePreset, setDatePreset] = useState<DatePreset>('1w');
  const [dateRange, setDateRange] = useState<GiftOrderListDateRange>(getDatePreset('1w'));
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [modalCouponOrder, setModalCouponOrder] = useState<GiftOrderForAdmin | null>(
    null,
  );
  const [modalDeliveryOrder, setModalDeliveryOrder] = useState<GiftOrderForAdmin | null>(
    null,
  );
  const [showDeliveryTestModal, setShowDeliveryTestModal] = useState(false);
  const [showCouponTestModal, setShowCouponTestModal] = useState(false);
  const [forceDeleteOrder, setForceDeleteOrder] = useState<GiftOrderForAdmin | null>(
    null,
  );

  const filterKey = `${dateRange.from ?? 'all'}_${dateRange.to ?? 'all'}`;

  const setOrderListStatus = useSetRecoilState(
    giftOrderListForAdminStatusAtomFamily(filterKey),
  );

  const handleDatePreset = useCallback((preset: DatePreset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      setDateRange(getDatePreset(preset));
    }
  }, []);

  const handleCustomDateApply = useCallback(() => {
    if (customFrom || customTo) {
      setDateRange({
        from: customFrom || undefined,
        to: customTo || undefined,
      });
    }
  }, [customFrom, customTo]);

  const handleStatusClick = useCallback((order: GiftOrderForAdmin) => {
    setForceDeleteOrder(order);
  }, []);

  const handleForceDeleted = useCallback(() => {
    setForceDeleteOrder(null);
    setOrderListStatus('idle');
  }, [setOrderListStatus]);

  const handleCouponClick = useCallback(
    (_coupons: GiftCouponSummary[], order: GiftOrderForAdmin) => {
      setModalCouponOrder(order);
    },
    [],
  );

  const handleDeliveryClick = useCallback(
    (_deliveries: GiftDeliverySummary[], order: GiftOrderForAdmin) => {
      setModalDeliveryOrder(order);
    },
    [],
  );

  useGiftOrderListForAdminHook(filterKey, {}, false, dateRange);
  const status = useRecoilValue(giftOrderListForAdminStatusAtomFamily(filterKey));
  const orderList = useRecoilValue(giftOrderListForAdminDataAtomFamily(filterKey));

  const filteredOrders = useMemo(() => {
    if (!orderList) return [];

    let filtered = [...orderList];

    if (statusFilter === 'paid') {
      filtered = filtered.filter(
        (order) => order.paymentDetail.status === GiftOrderPaymentStatus.Paid,
      );
    } else if (statusFilter === 'confirmed') {
      filtered = filtered.filter(
        (order) => order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed,
      );
    } else if (statusFilter === 'cancelRequested') {
      filtered = filtered.filter(
        (order) => order.paymentDetail.status === GiftOrderPaymentStatus.CancelRequested,
      );
    } else if (statusFilter === 'canceled') {
      filtered = filtered.filter(
        (order) => order.paymentDetail.status === GiftOrderPaymentStatus.Canceled,
      );
    }

    if (typeFilter === 'coupon') {
      filtered = filtered.filter((order) => {
        const type = getOrderType(order);
        return type === 'coupon' || type === 'mixed';
      });
    } else if (typeFilter === 'delivery') {
      filtered = filtered.filter((order) => {
        const type = getOrderType(order);
        return type === 'delivery' || type === 'mixed';
      });
    }

    return filtered;
  }, [orderList, statusFilter, typeFilter]);

  const stats = useMemo(() => {
    const empty = {
      total: 0,
      totalAmount: 0,
      paidCount: 0,
      paidAmount: 0,
      confirmedCount: 0,
      confirmedAmount: 0,
      cancelRequestedCount: 0,
      cancelRequestedAmount: 0,
      canceledCount: 0,
      canceledAmount: 0,
      couponCount: 0,
      couponAmount: 0,
      deliveryCount: 0,
      deliveryAmount: 0,
      individualCount: 0,
      individualAmount: 0,
      bulkCount: 0,
      bulkAmount: 0,
      mixedCount: 0,
      mixedAmount: 0,
    };
    if (!orderList) return empty;

    const s = { ...empty, total: orderList.length };

    orderList.forEach((order) => {
      const amount = order.paymentDetail.paymentByCash;
      s.totalAmount += amount;

      const payStatus = order.paymentDetail.status;
      if (payStatus === GiftOrderPaymentStatus.Paid) {
        s.paidCount++;
        s.paidAmount += amount;
      } else if (payStatus === GiftOrderPaymentStatus.Confirmed) {
        s.confirmedCount++;
        s.confirmedAmount += amount;
      } else if (payStatus === GiftOrderPaymentStatus.CancelRequested) {
        s.cancelRequestedCount++;
        s.cancelRequestedAmount += amount;
      } else if (payStatus === GiftOrderPaymentStatus.Canceled) {
        s.canceledCount++;
        s.canceledAmount += amount;
      }

      const type = getOrderType(order);
      if (type === 'coupon') {
        s.couponCount++;
        s.couponAmount += amount;
      } else if (type === 'delivery') {
        s.deliveryCount++;
        s.deliveryAmount += amount;
        if (order.deliveryMode === GiftOrderDeliveryMode.Individual) {
          s.individualCount++;
          s.individualAmount += amount;
        } else {
          s.bulkCount++;
          s.bulkAmount += amount;
        }
      } else {
        s.mixedCount++;
        s.mixedAmount += amount;
      }
    });

    return s;
  }, [orderList]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">주문 관리</h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg bg-white px-4 py-3 shadow">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-gray-500">전체</span>
            <span className="text-sm font-bold text-gray-900">{stats.total}건</span>
            <span className="text-sm font-bold text-gray-900">
              {formatPrice(stats.totalAmount)}원
            </span>
          </div>

          <div className="h-4 border-l border-gray-200" />

          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-green-600">결제완료</span>
            <span className="text-sm font-bold text-green-700">{stats.paidCount}건</span>
            <span className="text-sm font-bold text-green-600">
              {formatPrice(stats.paidAmount)}원
            </span>
          </div>

          <div className="h-4 border-l border-gray-200" />

          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-emerald-600">확정</span>
            <span className="text-sm font-bold text-emerald-700">
              {stats.confirmedCount}건
            </span>
            <span className="text-sm font-bold text-emerald-600">
              {formatPrice(stats.confirmedAmount)}원
            </span>
          </div>

          {stats.cancelRequestedCount > 0 && (
            <>
              <div className="h-4 border-l border-gray-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-orange-600">취소요청</span>
                <span className="text-sm font-bold text-orange-700">
                  {stats.cancelRequestedCount}건
                </span>
                <span className="text-sm font-bold text-orange-600">
                  {formatPrice(stats.cancelRequestedAmount)}원
                </span>
              </div>
            </>
          )}

          {stats.canceledCount > 0 && (
            <>
              <div className="h-4 border-l border-gray-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-red-600">취소됨</span>
                <span className="text-sm font-bold text-red-700">
                  {stats.canceledCount}건
                </span>
                <span className="text-sm font-bold text-red-600">
                  {formatPrice(stats.canceledAmount)}원
                </span>
              </div>
            </>
          )}

          <div className="h-4 border-l border-gray-300" />

          <div
            className="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-0.5 transition-colors hover:bg-purple-50"
            onClick={() => setShowCouponTestModal(true)}
            title="[TEST] 클릭하여 Coupon 생성"
          >
            <span className="text-xs font-medium text-purple-600">
              쿠폰형<span className="ml-0.5 text-[10px] text-red-400">[T]</span>
            </span>
            <span className="text-sm font-bold text-purple-700">
              {stats.couponCount}건
            </span>
            <span className="text-sm font-bold text-purple-600">
              {formatPrice(stats.couponAmount)}원
            </span>
          </div>

          <div className="h-4 border-l border-gray-200" />

          <div
            className="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-0.5 transition-colors hover:bg-blue-50"
            onClick={() => setShowDeliveryTestModal(true)}
            title="[TEST] 클릭하여 Delivery 생성"
          >
            <span className="text-xs font-medium text-blue-600">
              배송형<span className="ml-0.5 text-[10px] text-red-400">[T]</span>
            </span>
            <span className="text-sm font-bold text-blue-700">
              {stats.deliveryCount}건
            </span>
            <span className="text-sm font-bold text-blue-600">
              {formatPrice(stats.deliveryAmount)}원
            </span>
            {stats.deliveryCount > 0 && (
              <span className="ml-1 flex items-center gap-2 border-l border-blue-100 pl-2 text-[11px]">
                <span>
                  <span className="text-teal-600">개별</span>{' '}
                  <span className="font-bold text-teal-700">{stats.individualCount}</span>
                </span>
                <span>
                  <span className="text-gray-500">일괄</span>{' '}
                  <span className="font-bold text-gray-700">{stats.bulkCount}</span>
                </span>
              </span>
            )}
          </div>

          {stats.mixedCount > 0 && (
            <>
              <div className="h-4 border-l border-gray-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-amber-600">혼합</span>
                <span className="text-sm font-bold text-amber-700">
                  {stats.mixedCount}건
                </span>
                <span className="text-sm font-bold text-amber-600">
                  {formatPrice(stats.mixedAmount)}원
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 필터 */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex gap-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              statusFilter === 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            전체
          </button>
          <button
            onClick={() => setStatusFilter('paid')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              statusFilter === 'paid'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            결제완료
          </button>
          <button
            onClick={() => setStatusFilter('confirmed')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              statusFilter === 'confirmed'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            확정
          </button>
          <button
            onClick={() => setStatusFilter('cancelRequested')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              statusFilter === 'cancelRequested'
                ? 'bg-orange-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            취소요청
          </button>
          <button
            onClick={() => setStatusFilter('canceled')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              statusFilter === 'canceled'
                ? 'bg-red-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            취소됨
          </button>
        </div>

        <div className="mx-2 border-l border-gray-300" />

        <div className="flex gap-2">
          <button
            onClick={() => setTypeFilter('all')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              typeFilter === 'all'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            전체유형
          </button>
          <button
            onClick={() => setTypeFilter('coupon')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              typeFilter === 'coupon'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            쿠폰형
          </button>
          <button
            onClick={() => setTypeFilter('delivery')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              typeFilter === 'delivery'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            배송형
          </button>
        </div>
      </div>

      {/* 기간 필터 */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500">기간</span>
        <div className="flex gap-1">
          {(Object.keys(DATE_PRESET_LABELS) as DatePreset[])
            .filter((k) => k !== 'custom')
            .map((preset) => (
              <button
                key={preset}
                onClick={() => handleDatePreset(preset)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                  datePreset === preset
                    ? 'bg-gray-800 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {DATE_PRESET_LABELS[preset]}
              </button>
            ))}
          <button
            onClick={() => handleDatePreset('custom')}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              datePreset === 'custom'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            직접선택
          </button>
        </div>
        {datePreset === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
            />
            <span className="text-xs text-gray-400">~</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
            />
            <button
              onClick={handleCustomDateApply}
              disabled={!customFrom && !customTo}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              조회
            </button>
          </div>
        )}
        {dateRange.from && (
          <span className="text-xs text-gray-400">
            {dateRange.from} ~ {dateRange.to ?? '현재'}
          </span>
        )}
      </div>

      {/* 테이블 */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  결제일시
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  상품명
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  유형
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  결제금액
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  주문자
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  고인명
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  상조
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium tracking-wider text-gray-500">
                  <span className="uppercase">쿠폰</span>
                  <span className="ml-1 text-[10px] font-normal normal-case text-gray-400">
                    (전체/미공유/미사용/교환)
                  </span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium tracking-wider text-gray-500">
                  <span className="uppercase">배송</span>
                  <span className="ml-1 text-[10px] font-normal normal-case text-gray-400">
                    (전체/대기/준비/배송/완료)
                  </span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  상태
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-gray-500">
                    주문이 없습니다.
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <OrderRow
                    key={order._id}
                    order={order}
                    onCouponClick={(coupons) => handleCouponClick(coupons, order)}
                    onDeliveryClick={(deliveries) =>
                      handleDeliveryClick(deliveries, order)
                    }
                    onStatusClick={handleStatusClick}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalCouponOrder && modalCouponOrder.coupons && (
        <CouponDetailModal
          coupons={modalCouponOrder.coupons}
          orderId={modalCouponOrder._id}
          order={modalCouponOrder}
          onClose={() => setModalCouponOrder(null)}
        />
      )}

      {modalDeliveryOrder && modalDeliveryOrder.deliveries && (
        <DeliveryDetailModal
          deliveries={modalDeliveryOrder.deliveries as GiftDeliverySummary[]}
          orderId={modalDeliveryOrder._id}
          order={modalDeliveryOrder}
          onClose={() => setModalDeliveryOrder(null)}
        />
      )}

      {showDeliveryTestModal && orderList && (
        <CreateDeliveryTestModal
          orders={orderList}
          onClose={() => setShowDeliveryTestModal(false)}
        />
      )}

      {showCouponTestModal && orderList && (
        <CreateCouponTestModal
          orders={orderList}
          onClose={() => setShowCouponTestModal(false)}
        />
      )}

      {forceDeleteOrder && (
        <ForceDeleteModal
          order={forceDeleteOrder}
          onClose={() => setForceDeleteOrder(null)}
          onDeleted={handleForceDeleted}
        />
      )}
    </div>
  );
};
